import React from 'react';

/**
 * Simplified MetricCard Props for dashboard metrics
 * Removes fake trend/percentage data in favor of real operational data
 */
export interface SimpleMetricCardProps {
  /** Card title */
  title: string;
  /** Primary value to display */
  value: number | string;
  /** Unit or subtitle (e.g., "files", "healthy", "enabled") */
  subtitle?: string;
  /** Denominator for fraction display: value / denominator */
  denominator?: number;
  /** Secondary text line (e.g., "45 OK, 2 failed") */
  secondaryText?: string;
  /** Status determines card accent color */
  status?: 'success' | 'warning' | 'error' | 'neutral';
  /** Icon to display */
  icon?: React.ReactNode;
  /** Loading state */
  isLoading?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Shimmer animation classes for skeleton loading state
 */
const shimmerClasses =
  'animate-shimmer bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 bg-[length:200%_100%] dark:from-neutral-800 dark:via-neutral-700 dark:to-neutral-800';

/**
 * Format large numbers with abbreviations (K, M, B)
 */
const formatValue = (value: number | string): string => {
  if (typeof value === 'string') return value;

  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (value >= 10_000) {
    return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  if (value >= 1_000) {
    return value.toLocaleString();
  }
  return value.toString();
};

/**
 * Get status-based color classes
 */
const getStatusClasses = (status: SimpleMetricCardProps['status']): { text: string; bg: string; border: string } => {
  switch (status) {
    case 'success':
      return {
        text: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-50 dark:bg-emerald-900/30',
        border: 'border-emerald-200 dark:border-emerald-800',
      };
    case 'warning':
      return {
        text: 'text-amber-600 dark:text-amber-400',
        bg: 'bg-amber-50 dark:bg-amber-900/30',
        border: 'border-amber-200 dark:border-amber-800',
      };
    case 'error':
      return {
        text: 'text-red-600 dark:text-red-400',
        bg: 'bg-red-50 dark:bg-red-900/30',
        border: 'border-red-200 dark:border-red-800',
      };
    default:
      return {
        text: 'text-neutral-600 dark:text-neutral-400',
        bg: 'bg-neutral-50 dark:bg-neutral-800',
        border: 'border-neutral-200 dark:border-neutral-700',
      };
  }
};

/**
 * Loading skeleton for MetricCard
 */
const MetricCardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    className={`
      bg-white rounded-xl shadow-sm border border-neutral-200 p-5
      dark:bg-slate-800 dark:border-slate-700 dark:shadow-dark
      ${className}
    `}
    role="status"
    aria-label="Loading metric"
  >
    <div className="flex items-start justify-between">
      <div className="flex-1">
        {/* Label skeleton */}
        <div className={`${shimmerClasses} rounded h-4 w-24 mb-3`} />
        {/* Value skeleton */}
        <div className={`${shimmerClasses} rounded h-9 w-32 mb-3`} />
        {/* Secondary text skeleton */}
        <div className={`${shimmerClasses} rounded h-4 w-16`} />
      </div>
      {/* Icon skeleton */}
      <div className={`${shimmerClasses} rounded-lg h-12 w-12`} />
    </div>
  </div>
);

/**
 * Simplified MetricCard component for dashboard metrics
 *
 * Displays a metric with its current value and optional fraction/secondary text.
 * No fake trends or percentages - just real operational data.
 *
 * @example
 * ```tsx
 * <MetricCard
 *   title="Connections"
 *   value={3}
 *   denominator={4}
 *   subtitle="healthy"
 *   secondaryText="1 issue"
 *   status="warning"
 *   icon={<ConnectionIcon />}
 * />
 * ```
 */
export const MetricCard: React.FC<SimpleMetricCardProps> = ({
  title,
  value,
  subtitle,
  denominator,
  secondaryText,
  status = 'neutral',
  icon,
  isLoading = false,
  onClick,
  className = '',
}) => {
  // Show loading skeleton
  if (isLoading) {
    return <MetricCardSkeleton className={className} />;
  }

  const formattedValue = formatValue(value);
  const statusClasses = getStatusClasses(status);
  const isClickable = !!onClick;

  // Build base card classes
  const cardClasses = [
    // Base styles
    'relative overflow-hidden',
    'bg-white rounded-xl shadow-sm border border-neutral-200 p-5',
    'dark:bg-slate-800 dark:border-slate-700',
    // Transition for hover
    'transition-all duration-200',
    // Group for child hover states
    'group',
  ];

  // Interactive styles
  if (isClickable) {
    cardClasses.push(
      'cursor-pointer',
      'hover:shadow-md hover:border-neutral-300 hover:-translate-y-0.5',
      'dark:hover:border-slate-600',
      'active:scale-[0.99] active:shadow-sm'
    );
  }

  const CardElement = isClickable ? 'button' : 'div';
  const cardProps = isClickable
    ? {
        type: 'button' as const,
        onClick,
        'aria-label': `View details for ${title}`,
      }
    : {};

  return (
    <CardElement
      className={`${cardClasses.join(' ')} ${className} text-left w-full`}
      {...cardProps}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Content section */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <p className="text-sm font-medium text-neutral-500 dark:text-slate-400 mb-1 truncate uppercase tracking-wide">
            {title}
          </p>

          {/* Value with optional denominator (fraction display) */}
          <div className="flex items-baseline gap-1.5 mb-1">
            <span
              className="
                text-3xl font-mono font-bold text-neutral-900 dark:text-white
                transition-transform duration-300
                group-hover:scale-[1.02] origin-left
              "
            >
              {formattedValue}
            </span>
            {denominator !== undefined && (
              <span className="text-lg font-mono text-neutral-400 dark:text-slate-500">
                / {denominator}
              </span>
            )}
          </div>

          {/* Subtitle (e.g., "files", "healthy") */}
          {subtitle && (
            <p className="text-sm text-neutral-500 dark:text-slate-400 mb-1">
              {subtitle}
            </p>
          )}

          {/* Secondary text with status color */}
          {secondaryText && (
            <p className={`text-xs font-medium ${statusClasses.text}`}>
              {secondaryText}
            </p>
          )}
        </div>

        {/* Icon section */}
        {icon && (
          <div
            className={`
              flex-shrink-0 p-3 rounded-xl
              ${statusClasses.bg}
              transition-all duration-200
              group-hover:scale-105
            `}
          >
            <span className={statusClasses.text}>{icon}</span>
          </div>
        )}
      </div>

      {/* Status indicator bar at bottom */}
      {status !== 'neutral' && (
        <div
          className={`
            absolute bottom-0 left-0 right-0 h-1
            ${status === 'success' ? 'bg-emerald-500' : status === 'warning' ? 'bg-amber-500' : 'bg-red-500'}
          `}
        />
      )}

      {/* Hover indicator line for clickable */}
      {isClickable && status === 'neutral' && (
        <div
          className="
            absolute bottom-0 left-0 right-0 h-0.5
            bg-gradient-to-r from-primary-500 to-primary-600
            transform scale-x-0 group-hover:scale-x-100
            transition-transform duration-300 origin-left
          "
        />
      )}
    </CardElement>
  );
};

export default MetricCard;
export type { SimpleMetricCardProps as MetricCardProps };
